import {
  Checkbox,
  checkboxStates,
  GenericTable,
  Input,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { VIRTUALIZE_TABLE } from "constants/app";
import { PHASE_BY_EVENT } from "constants/outlineGeneration";
import { PlusIcon } from "lucide-react";
import { func } from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import {
  selectClarification,
  selectGateState,
  selectOutlineDocuments,
  selectRequirements,
  selectStructureProposal,
  selectVerificationCounts,
} from "slices/proposalOutline/selectors";
import {
  mergeAnswersLastWins,
  safeJsonParse,
} from "slices/proposalOutline/utils";

import PhaseStepper from "../components/OutlineGenerationProgress/PhaseStepper";
import gateStyles from "./Gate.module.scss";
import PlannerBlock from "./PlannerBlock";
import {
  buildDocumentLabelsById,
  buildRequirementColumns,
} from "./requirementsReviewColumns";
import styles from "./RequirementsReviewGate.module.scss";
import StructureEditor from "./StructureEditor";

const SUBMIT_ANSWERS_ERROR_TITLE =
  "There was a problem submitting your answers.";
const RECLASSIFY_CHECKBOX_ID = "requirements-review-reclassify";
const GATE_REQ_REVIEW = "REQ_REVIEW";

const RequirementsReviewGate = ({
  onConfirm,
  onSubmitAnswers,
  onAddRequirement,
  onUpdateRequirement,
  onRemoveRequirement,
  onReExtract,
}) => {
  const dispatch = useDispatch();
  const requirements = useSelector(selectRequirements);
  const documents = useSelector(selectOutlineDocuments);
  const counts = useSelector(selectVerificationCounts);
  const clarification = useSelector(selectClarification);
  const gateState = useSelector(selectGateState);
  const structureProposal = useSelector(selectStructureProposal);

  const [tierLabel, setTierLabel] = useState("");
  const [groupTitles, setGroupTitles] = useState([]);

  useEffect(() => {
    setTierLabel(structureProposal?.tierLabel ?? "");
    setGroupTitles((structureProposal?.groups ?? []).map((g) => g.title));
  }, [structureProposal]);

  const buildStructurePayload = () => ({
    tierLabel,
    groups: groupTitles
      .map((t) => t.trim())
      .filter(Boolean)
      .map((title) => ({ title })),
  });

  const persistedBlock = useMemo(() => {
    if (gateState?.gate !== GATE_REQ_REVIEW) {
      return null;
    }
    return safeJsonParse(gateState?.block);
  }, [gateState]);

  const initialBlock = clarification || persistedBlock;

  const [block, setBlock] = useState(initialBlock);
  // Same rationale as TypeReviewGate: this gate's own rounds are tracked locally
  // rather than reusing Redux's clarificationAnswers, which accumulates across the
  // whole outline run (including an unrelated AWAITING_CLARIFICATION round).
  const [submittedAnswers, setSubmittedAnswers] = useState([]);
  const [newRestatedText, setNewRestatedText] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [reclassify, setReclassify] = useState(false);

  // Keyed on the block's IDENTITY, not the object — see the matching comment in TypeReviewGate.
  // Every SSE reconnect replays req_review_ready with a freshly-parsed block, so keying on
  // initialBlock itself reset a user mid-answer back to round 1 and remounted PlannerBlock.
  const initialBlockRef = useRef(initialBlock);
  initialBlockRef.current = initialBlock;
  const initialBlockId = initialBlock?.blockId ?? null;
  const initialRound = initialBlock?.round ?? null;

  useEffect(() => {
    setBlock(initialBlockRef.current);
    setSubmittedAnswers([]);
  }, [initialBlockId, initialRound]);

  const documentLabelsById = useMemo(
    () => buildDocumentLabelsById(documents),
    [documents]
  );

  const columns = useMemo(
    () =>
      buildRequirementColumns({
        documentLabelsById,
        onUpdate: onUpdateRequirement,
        onRemove: onRemoveRequirement,
      }),
    [documentLabelsById, onUpdateRequirement, onRemoveRequirement]
  );

  const handleSubmit = async (answers) => {
    try {
      const response = await onSubmitAnswers(answers);
      const followUp = response?.followUpBlock
        ? JSON.parse(response.followUpBlock)
        : null;
      setSubmittedAnswers((prev) => mergeAnswersLastWins(prev, answers));
      setBlock(followUp);
    } catch (error) {
      dispatch(showErrorModal(error, SUBMIT_ANSWERS_ERROR_TITLE));
    }
  };

  const handleAdd = async () => {
    const restatedText = newRestatedText.trim();
    if (!restatedText) {
      return;
    }
    const created = await onAddRequirement({
      restatedText,
      category: newCategory.trim() || null,
    });
    if (created) {
      setNewRestatedText("");
      setNewCategory("");
    }
  };

  const handleReExtract = () => onReExtract(reclassify);

  return (
    <div className={gateStyles.gate} data-testid="requirements-review-gate">
      <PhaseStepper
        currentPhase={PHASE_BY_EVENT.req_review_ready}
        failed={false}
        awaitingGate
      />

      <h3>Review Requirements</h3>

      <p className={styles.summary}>
        {counts.total} Requirements &middot; {counts.verified} Verified &middot;{" "}
        {counts.fuzzyMatch} Partial Match &middot; {counts.unverified}{" "}
        Unverified &middot; {counts.userProvided} Added
      </p>

      <div className={styles.tableWrapper}>
        <GenericTable
          columns={columns}
          data={requirements}
          emptyMessage="No requirements extracted yet."
          isVirtualized={VIRTUALIZE_TABLE}
        />
      </div>

      <div className={styles.addRow}>
        <textarea
          aria-label="New Requirement Text"
          className={styles.addRowTextarea}
          onChange={(e) => setNewRestatedText(e.target.value)}
          placeholder="Add A New Requirement..."
          rows={2}
          value={newRestatedText}
        />
        <div className={styles.addRowCategory}>
          <Input
            aria-label="New Requirement Category"
            onChange={setNewCategory}
            placeholder="Category"
            value={newCategory}
          />
        </div>
        <NewButton
          leftIcon={<PlusIcon size={16} />}
          onClick={handleAdd}
          variant={NewButtonVariant.l2}
        >
          Add Requirement
        </NewButton>
      </div>

      {block && (
        <div className={styles.plannerRound}>
          <PlannerBlock
            key={block.blockId}
            block={block}
            priorAnswers={submittedAnswers}
            onSubmit={handleSubmit}
          />
        </div>
      )}

      <StructureEditor
        tierLabel={tierLabel}
        groups={groupTitles}
        onChange={({ tierLabel: t, groups }) => {
          setTierLabel(t);
          setGroupTitles(groups);
        }}
      />

      <div className={styles.actions}>
        <NewButton
          onClick={() => onConfirm(buildStructurePayload())}
          variant={NewButtonVariant.l1}
        >
          Confirm & Generate
        </NewButton>

        <div className={styles.reExtractRow}>
          <Checkbox
            ariaLabel="Reclassify Document Types"
            handleChange={() => setReclassify((prev) => !prev)}
            id={RECLASSIFY_CHECKBOX_ID}
            label="Reclassify Document Types"
            state={reclassify ? checkboxStates.checked : checkboxStates.empty}
          />
          <NewButton onClick={handleReExtract} variant={NewButtonVariant.l3}>
            Re-Extract
          </NewButton>
        </div>
      </div>
    </div>
  );
};

RequirementsReviewGate.propTypes = {
  onAddRequirement: func.isRequired,
  onConfirm: func.isRequired,
  onRemoveRequirement: func.isRequired,
  onReExtract: func.isRequired,
  onSubmitAnswers: func.isRequired,
  onUpdateRequirement: func.isRequired,
};

export default RequirementsReviewGate;
